import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { ExampleTable } from "@/help/guides/examples/example-ui";
import { InlineConfirmation } from "@/payroll/ai-script/ai-script-persistent-edits/inline-confirmation";
import type { RerunDecision, RerunScenario } from "./rerun-scenarios";

const CANDIDATES = [
  { cell: "C2", base: "$1,200", bonus: "$300", id: "2:3" },
  { cell: "C3", base: "$1,350", bonus: "$150", id: "3:3" },
] as const;

export const RerunReviewActions = ({
  scenario,
  decision,
  pickerOpen,
  confirmDiscard,
  onPickRow,
  onDiscard,
  onCancel,
  onResolve,
}: {
  scenario: RerunScenario;
  decision: RerunDecision;
  pickerOpen: boolean;
  confirmDiscard: boolean;
  onPickRow: () => void;
  onDiscard: () => void;
  onCancel: () => void;
  onResolve: (decision: RerunDecision) => void;
}) => {
  if (decision || scenario === "applied" || scenario === "dropped") return null;
  if (confirmDiscard) {
    return (
      <InlineConfirmation
        message={
          scenario === "matching"
            ? "Discard this edit? Neither matching row will receive the saved $250 bonus."
            : "Discard this edit? The cell goes back to the value the payroll script produced."
        }
        confirmLabel="Discard edit"
        isPending={false}
        onCancel={onCancel}
        onConfirm={() => onResolve("discard")}
      />
    );
  }
  return (
    <>
      <Stack
        className="w-full"
        align="start"
        direction="row"
        gap={2}
        justify="end"
      >
        <Button variant="outline" size="xs" onClick={onDiscard}>
          Discard
        </Button>
        {scenario === "matching" ? (
          <Button variant="secondary" size="xs" onClick={onPickRow}>
            Pick row
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="xs"
            onClick={() => onResolve("keep")}
          >
            Keep edit
          </Button>
        )}
      </Stack>
      {pickerOpen && scenario === "matching" && (
        <Stack className="w-full" gap={2} align="stretch">
          <p className="font-semibold">Which row is this edit for?</p>
          <ExampleTable
            caption="Matching sample rows for Alex Morgan"
            headers={["Cell", "Base pay", "Current bonus", "Action"]}
            rows={CANDIDATES.map(({ cell, base, bonus, id }) => [
              cell,
              base,
              bonus,
              <Button
                key={id}
                variant="secondary"
                size="xs"
                aria-label={`Use ${cell} for Alex Morgan`}
                onClick={() => onResolve(id)}
              >
                Use this row
              </Button>,
            ])}
          />
        </Stack>
      )}
    </>
  );
};
